"use client";
import React, { useEffect, useState } from 'react';
import { hasAdsConsent } from '@/components/common/CookieBanner';

interface AdPlacementProps {
  position: 'sidebar' | 'footer' | 'content-top' | 'content-bottom';
  className?: string;
  slotId?: string; // allow overriding slot id per placement
  adLabel?: boolean; // render small "Advertisement" label
}

declare global {
  interface Window {
    ezstandalone: any;
  }
}

// Default Ezoic Placeholder IDs based on standard Ezoic auto-generated lists.
const positionToDefaultSlot: Record<AdPlacementProps['position'], string> = {
  sidebar: process.env.NEXT_PUBLIC_EZOIC_SLOT_SIDEBAR || '104',
  footer: process.env.NEXT_PUBLIC_EZOIC_SLOT_FOOTER || '103',
  'content-top': process.env.NEXT_PUBLIC_EZOIC_SLOT_TOP || '101',
  'content-bottom': process.env.NEXT_PUBLIC_EZOIC_SLOT_BOTTOM || '111',
};

const AdPlacement: React.FC<AdPlacementProps> = ({ position, className = '', slotId, adLabel = true }) => {
  const [canRender, setCanRender] = useState(false);
  const placeholderId = slotId || positionToDefaultSlot[position];

  useEffect(() => {
    const onConsentChange = () => setCanRender(hasAdsConsent());
    setCanRender(hasAdsConsent());
    window.addEventListener('consentchange', onConsentChange as EventListener);

    return () => window.removeEventListener('consentchange', onConsentChange as EventListener);
  }, []);

  useEffect(() => {
    if (!canRender || !placeholderId) return;

    // Ezoic requires re-triggering for SPA navigation
    const showAd = () => {
      if (window.ezstandalone && window.ezstandalone.cmd) {
        window.ezstandalone.cmd.push(function() {
          // If the placeholder is already defined, we just show it
          // Otherwise, we define it first
          if (typeof window.ezstandalone.define === 'function') {
            window.ezstandalone.define(parseInt(placeholderId));
          }
          window.ezstandalone.showAds(parseInt(placeholderId));
        });
      }
    };

    // Small delay to ensure DOM is ready in Next.js
    const timer = setTimeout(showAd, 100);
    return () => clearTimeout(timer);
  }, [canRender, placeholderId]);

  return (
    <div className={`ad-container ${position} ${className} overflow-hidden min-h-[90px]`}>
      {adLabel && <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Advertisement</div>}
      {canRender && (
        <div id={`ezoic-pub-ad-placeholder-${placeholderId}`}></div>
      )}
    </div>
  );
};

export default AdPlacement; 